'use client';

import { forkPublicShare } from '@/app/actions/share';
import { Button, type ButtonProps } from '@/components/ui/button';
import { useAuth } from '@/providers/auth-provider';
import { LoaderCircleIcon } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useState } from 'react';

type Props = ButtonProps & {
  shareToken: string;
  topicTitle?: string;
  loadingText?: string;
};

export default function ContinueSearchButton({
  shareToken,
  topicTitle,
  children,
  loadingText,
  disabled,
  ...props
}: Props) {
  const router = useRouter();
  const { user, loading, triggerLogin } = useAuth();
  const [forking, setForking] = useState(false);

  const handleContinueSearch = async () => {
    if (forking || loading) {
      return;
    }
    const query = topicTitle?.trim();
    if (query) {
      router.push(`/?q=${encodeURIComponent(query)}`);
      return;
    }
    if (!user) {
      triggerLogin?.();
      return;
    }
    setForking(true);
    try {
      const result = await forkPublicShare(shareToken);
      router.push(`/search?sid=${result.session_id}`);
    } catch (error) {
      console.error(error);
    } finally {
      setForking(false);
    }
  };

  return (
    <Button
      {...props}
      disabled={disabled || forking || loading}
      onClick={() => void handleContinueSearch()}
    >
      {forking ? (
        <>
          <LoaderCircleIcon className="h-4 w-4 animate-spin" />
          {loadingText ?? children}
        </>
      ) : (
        children
      )}
    </Button>
  );
}
